import { clsx } from 'clsx'
import { memo, useEffect, useRef, useState } from 'react'
import { rotatedSize } from '@/lib/geometry'
import { getThumb } from '@/lib/pdf/thumbs'
import { useDoc } from '@/store/documentStore'
import type { Annotation, PageRef } from '@/types'
import { isMarkup, MarkupShape, SvgShape } from '../viewer/AnnotationViews'

const EMPTY: Annotation[] = []

/** Lazily rendered page thumbnail that also previews annotations. */
export const PageThumb = memo(function PageThumb({
  page,
  width,
  className,
}: {
  page: PageRef
  width: number
  className?: string
}) {
  const [url, setUrl] = useState('')
  const [visible, setVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const anns = useDoc((s) => s.annotations[page.id] ?? EMPTY)
  const { w, h } = rotatedSize(page)
  const height = (width * h) / w
  const k = width / w

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setVisible(true), {
      rootMargin: '300px',
    })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (!visible) return
    let alive = true
    const base = page.rotation % 180 === 0 ? width : height
    getThumb(page, base).then((u) => alive && setUrl(u))
    return () => {
      alive = false
    }
  }, [visible, page, width, height])

  const innerW = page.width * k
  const innerH = page.height * k

  return (
    <div ref={ref} className={clsx('relative overflow-hidden bg-white', className)} style={{ width, height }}>
      <div
        className="absolute"
        style={{
          width: innerW,
          height: innerH,
          left: '50%',
          top: '50%',
          transform: `translate(-50%, -50%) rotate(${page.rotation}deg)`,
        }}
      >
        {url ? (
          <img src={url} alt="" draggable={false} className="absolute inset-0 size-full" />
        ) : page.srcId ? (
          <div className="absolute inset-0 animate-pulse bg-black/[0.04]" />
        ) : null}
        {anns.length > 0 && (
          <svg
            className="absolute inset-0 size-full"
            viewBox={`0 0 ${page.width} ${page.height}`}
            preserveAspectRatio="none"
            aria-hidden
          >
            <g style={{ mixBlendMode: 'multiply' }}>
              {anns.filter(isMarkup).map((a) => (
                <MarkupShape key={a.id} a={a} />
              ))}
            </g>
            {anns.map((a) =>
              a.type === 'text' || a.type === 'textEdit' ? (
                <rect
                  key={a.id}
                  x={a.x}
                  y={a.y}
                  width={a.w}
                  height={a.h}
                  fill={a.type === 'textEdit' ? a.cover : (a.background ?? 'none')}
                  stroke={a.type === 'text' ? a.color : 'none'}
                  strokeOpacity={0.35}
                />
              ) : a.type === 'note' ? (
                <rect key={a.id} x={a.x} y={a.y} width={22} height={22} rx={4} fill={a.color} />
              ) : (
                <SvgShape key={a.id} a={a} interactive={false} />
              ),
            )}
          </svg>
        )}
      </div>
    </div>
  )
})
